import React from 'react';
import { useQueryClient } from 'react-query';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import { useNavigation } from '../../hooks/useNavigation';
import { REVIEW_FIELDS_DICT, ReviewStep, STEP_ID } from './Review.meta';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { useCharitableGivingMutation } from '../../api/useCharitableGivingMutation';
import { useClientId } from '../../../../../_hooks/useClientId';
import { DonorReview } from '../Donor/Donor.review';
import { KindOfPropertyReview } from '../KindOfProperty/KindOfProperty.review';
import { DonationAmountReview } from '../DonationAmount/DonationAmount.review';
import { DonationYearReview } from '../DonationYear/DonationYear.review';
import { DafGrantYearReview } from '../DafGrantYear/DafGrantYear.review';
import { DafGrantAmountReview } from '../DafGrantAmount/DafGrantAmount.review';
import { CharityNameReview } from '../CharityName/CharityName.review';
import { FundAccountsReview } from '../FundAccounts/FundAccounts.review';
import { useRouteParams } from '../../../../hooks/useRouteParams';
import { toast } from '../../../../../CommonComponents/OPL/Toast/Toast';
import { DafDonationYearReview } from '../DafDonationYear/DafDonationYear.review';
import { ACCOUNTS_QUERY_KEY } from '../../../../../Profile/Accounts/AccountsConstants';
import { ACCOUNT_PREFERENCES_QUERY_KEY } from '../../../../../Profile/Accounts/api/useAccountsQuery';

const COMPONENT_DICT: Record<ReviewStep, React.FC> = {
  donor: DonorReview,
  kindOfProperty: KindOfPropertyReview,
  donationAmount: DonationAmountReview,
  donationYear: DonationYearReview,
  dafDonationYear: DafDonationYearReview,
  dafGrantYear: DafGrantYearReview,
  dafGrantAmount: DafGrantAmountReview,
  charityName: CharityNameReview,
  fundAccounts: FundAccountsReview,
};

export const Review = () => {
  const queryClient = useQueryClient();
  const { workflow, setStep } = useCharitableGivingContext();

  const {
    mutateAsync: saveWorkflow,
    isLoading: isSavingWorkflow,
    error: saveWorkflowError,
  } = useCharitableGivingMutation();

  const clientId = useClientId();

  const { goalId } = useRouteParams();

  const { progress, isFirstStep, previousStep } = useNavigation({
    currentStep: STEP_ID,
  });

  const onSubmit = async evt => {
    evt.preventDefault();

    try {
      const response = await saveWorkflow({ clientId, model: workflow.data });

      queryClient.invalidateQueries([ACCOUNTS_QUERY_KEY, ACCOUNT_PREFERENCES_QUERY_KEY, clientId]);

      toast.info('Goal was saved successfully.');

      const routeGoalId = goalId || response.Goals?.[response.Goals.length - 1].Id;

      ADV.util.Router.go2('Goal', {
        clientId,
        goalId: routeGoalId,
      });
    } catch {
      // Errors are displayed in the alert
    }
  };

  if (workflow.data.givingStrategy === null) {
    throw new Error('[Review Step] Giving Strategy is not defined');
  }

  const reviewFields = REVIEW_FIELDS_DICT[workflow.data.givingStrategy];

  return (
    <StepContainer
      title="Please review the information below"
      onSubmit={onSubmit}
      alert={saveWorkflowError?.message}
      footer={
        <StepFooter
          isLoading={isSavingWorkflow}
          isFirstStep={isFirstStep}
          progress={progress}
          onPrevious={previousStep ? () => setStep(previousStep) : undefined}
        />
      }
    >
      <Box as="ul" display="flex" flexDirection="column" mx={-32} mb={-32} css="padding: 0">
        {reviewFields.map(step => {
          const Component = COMPONENT_DICT[step];

          if (Component) {
            return <Component key={step} />;
          }

          throw new Error(`No review component for step ${step}`);
        })}
      </Box>
    </StepContainer>
  );
};
